import cloneDeep from 'lodash/cloneDeep';
import ih from 'immutability-helper';

import * as types from './actionTypes';
import initialState from './initialState';
import { clearUiComponents, isUiComponentVisible } from './utils';
import {
  AttachmentsConfig,
  MessageOwner,
  MessageStatus,
  MetaPayload,
} from 'types';
import {
  isTargetMessage,
  updateMessageInThread,
} from 'store/utils/chatHelpers';

interface Message {
  owner: MessageOwner;
  type: string;
  message: string;
  client_message_id: string;
  timestamp: string;
  status: MessageStatus;
  isSensitive?: boolean;
}
interface actionInterface {
  type?: string;
  payload?: {
    id?: string;
    message?: Message;
    error?: string;
    isOpen?: boolean;
    isMinimized?: boolean;
    isSessionExpired?: boolean;
    isChatDisabled?: boolean;
    activeApiUrl?: string;
    failedUrl?: string;
    withAgent?: boolean;
    matchFeedbackType?: boolean;
    client_message_id?: string;
    status?: MessageStatus;
    sequence_id?: number;
    isDialogEnded?: boolean;
    file?: File;
    utterance?: string;
    file_name?: string;
    file_type?: string;
    file_size?: string;
    attachmentsConfig?: AttachmentsConfig;
    errorMessage?: string;
    debugInfo?: any;
    meta?: MetaPayload;
    isHistory?: boolean;
    isAgentConnecting?: boolean;
    isAgentConnected?: boolean;
    isSensitiveInput?: boolean;
  };
}

export default (
  state = cloneDeep(initialState),
  action: actionInterface = {},
) => {
  switch (action.type) {
    case types.START_CHAT_REQUEST:
      return ih(state, {
        data: {
          thread: {
            $apply: thread =>
              thread.length > 0
                ? thread.filter(item => item.type !== 'preloader')
                : [],
          },
        },
        start: {
          $merge: {
            isLoading: true,
            error: '',
          },
        },
      });
    case types.START_CHAT_SUCCESS:
      return ih(state, {
        data: {
          id: { $set: action.payload.id },
        },
        start: {
          $merge: {
            isLoaded: true,
            isLoading: false,
          },
        },
      });
    case types.START_CHAT_ERROR:
      return ih(state, {
        start: {
          $merge: {
            isLoading: false,
            error: action.payload.error,
          },
        },
      });
    case types.SAVE_USER_RESPONSE:
      return ih(state, {
        data: {
          thread: {
            $apply: thread => thread.concat([action.payload.message]),
          },
        },
      });
    case types.SAVE_BOT_RESPONSE:
      return ih(state, {
        data: {
          thread: {
            $apply: thread => {
              const messages =
                action.payload?.message?.status === MessageStatus.SENT
                  ? clearUiComponents(thread)
                  : thread;

              return messages
                .concat(action.payload.message)
                .map(isUiComponentVisible);
            },
          },
        },
      });
    case types.SAVE_BOT_TYPING_ON:
      return ih(state, {
        data: {
          thread: {
            $apply: thread =>
              thread
                .filter(item => item.type !== 'preloader')
                .concat([
                  {
                    type: 'preloader',
                    withAgent: state.withAgent,
                  },
                ]),
          },
        },
      });
    case types.SAVE_BOT_TYPING_OFF:
      return ih(state, {
        data: {
          thread: {
            $apply: thread => thread.filter(item => item.type !== 'preloader'),
          },
        },
      });
    case types.SEND_TEXT_MESSAGE_REQUEST:
      return ih(state, {
        ask: {
          $merge: {
            isLoading: true,
            error: '',
          },
        },
        isDialogEnded: { $set: false },
      });
    case types.SEND_TEXT_MESSAGE_SUCCESS:
      return ih(state, {
        ask: {
          isLoading: { $set: false },
        },
      });
    case types.SEND_TEXT_MESSAGE_ERROR:
      return ih(state, {
        ask: {
          $merge: {
            isLoading: false,
            error: action.payload.error,
          },
        },
      });
    case types.SEND_TYPING_REQUEST:
      return ih(state, {
        ask: {
          $merge: {
            isLoading: true,
            error: '',
          },
        },
      });
    case types.SEND_TYPING_SUCCESS:
      return ih(state, {
        ask: {
          isLoading: { $set: false },
        },
      });
    case types.SEND_TYPING_ERROR:
      return ih(state, {
        ask: {
          $merge: {
            isLoading: false,
            // ignore errors on typing requests
            error: '',
          },
        },
      });
    case types.STOP_CHAT:
      return ih(state, {
        ask: {
          isLoading: { $set: false },
        },
      });
    case types.OPEN_CHAT_BOX:
      return ih(state, {
        $merge: {
          isOpen: true,
          isMinimized: false,
        },
      });
    case types.START_RETRY_CONNECT:
      return ih(state, {
        $merge: {
          isRetrying: true,
        },
      });
    case types.STOP_RETRY_CONNECT:
      return ih(state, {
        $merge: {
          isRetrying: false,
        },
      });
    case types.TOGGLE_MINIMIZE_CHAT_BOX:
      return ih(state, {
        $merge: { isMinimized: action.payload.isMinimized },
      });
    case types.CLOSE_CHAT_BOX:
      return ih(state, {
        $set: initialState,
      });
    case types.RESTART_CHAT_REQUEST:
      return ih(state, {
        isOpen: { $set: true },
        isRestarted: { $set: true },
        isMinimized: { $set: false },
        isChatDisabled: { $set: false },
        isDialogEnded: { $set: false },
        isSessionExpired: { $set: false },
        isAgentConnecting: { $set: false },
        isAgentConnected: { $set: false },
        isAgentTimeout: { $set: false },
        isSensitiveInput: { $set: false },
        data: {
          thread: { $set: [] },
          data: { $set: initialState.data?.data },
        },
      });
    case types.RESTART_CHAT_SUCCESS:
      return ih(state, {
        isRestarted: { $set: false },
      });
    case types.UPDATE_DIALOG_ID: {
      return ih(state, {
        data: {
          id: { $set: action.payload.id },
        },
      });
    }
    case types.SET_DIALOG_META:
      return ih(state, {
        data: {
          thread: {
            $apply: thread => thread.concat([action.payload.message]),
          },
        },
      });
    case types.SET_SESSION_CHAT_DATA:
      return ih(state, {
        data: {
          data: { $set: action.payload },
        },
      });

    case types.SET_SESSION_EXPIRED:
      return ih(state, {
        isSessionExpired: { $set: true },
      });

    case types.SET_DIALOG_ENDED:
      return ih(state, {
        isDialogEnded: { $set: action.payload.isDialogEnded },
        isAgentConnecting: {
          $set: action.payload.isDialogEnded ? false : state.isAgentConnecting,
        },
        isAgentTimeout: {
          $set: action.payload.isDialogEnded ? state.isAgentTimeout : false,
        },
        start: {
          $merge: {
            isLoading: false,
          },
        },
      });
    case types.SET_DISABLE_CHAT:
      return ih(state, {
        isChatDisabled: { $set: action.payload.isChatDisabled },
      });
    case types.SET_ACTIVE_API_URL:
      return ih(state, {
        activeApiUrl: { $set: action.payload.activeApiUrl },
      });
    case types.SET_FAILED_API_URL:
      return ih(state, {
        failedApiUrls: { $push: [action.payload.failedUrl] },
      });
    case types.CLEAR_FAILED_API_URLS:
      return ih(state, {
        failedApiUrls: { $set: [] },
      });
    case types.SET_CHAT_WITH_AGENT:
      return ih(state, {
        withAgent: { $set: action.payload.withAgent },
      });
    case types.SET_FEEDBACK_FORM:
      return ih(state, {
        matchFeedbackType: { $set: action.payload.matchFeedbackType },
      });
    case types.SAVE_META_MESSAGE:
      return ih(state, {
        meta: { $set: action.payload.meta },
      });
    case types.UPDATE_THREAD_MESSAGE:
      const updatedThread = clearUiComponents(state.data.thread)
        .map(message =>
          isTargetMessage(message, action)
            ? updateMessageInThread(message, action)
            : message,
        )
        .sort((a, b) => a.sequence_id - b.sequence_id);

      return ih(state, {
        data: {
          thread: {
            $set: updatedThread,
          },
        },
      });
    case types.DELETE_THREAD_MESSAGE:
      return ih(state, {
        data: {
          thread: {
            $set: clearUiComponents(state.data.thread)
              .filter((msg: any) => msg.client_message_id !== action.payload)
              .sort((a, b) => a.sequence_id - b.sequence_id),
          },
        },
      });

    case types.SET_ATTACHMENTS_CONFIG:
      return ih(state, {
        attachmentsConfig: { $merge: action.payload.attachmentsConfig },
      });

    case types.SET_MESSAGE_ERROR:
      return ih(state, {
        data: {
          thread: {
            $apply: thread =>
              thread.map(threadItem => {
                if (
                  threadItem.type === 'file' &&
                  threadItem.status === MessageStatus.PENDING
                ) {
                  return {
                    ...threadItem,
                    errorMessage: action.payload.errorMessage,
                    status: MessageStatus.ERROR,
                  };
                }

                return threadItem;
              }),
          },
        },
      });
    case types.SET_IS_HISTORY:
      return ih(state, {
        isHistory: { $set: action.payload.isHistory },
      });
    case types.SET_DEBUG_INFO:
      return ih(state, {
        $merge: {
          debugInfo: action.payload,
        },
      });
    case types.SET_AGENT_CONNECTING:
      return ih(state, {
        isAgentConnecting: { $set: action.payload.isAgentConnecting },
        isAgentConnected: { $set: false },
        isAgentTimeout: {
          $set: action.payload.isAgentConnecting ? false : state.isAgentTimeout,
        },
      });
    case types.SET_AGENT_TIMEOUT:
      return ih(state, {
        isAgentTimeout: { $set: action.payload.isAgentTimeout },
      });
    case types.SET_AGENT_CONNECTED:
      return ih(state, {
        isAgentConnecting: { $set: false },
        isAgentConnected: { $set: action.payload.isAgentConnected },
        isAgentTimeout: {
          $set: action.payload.isAgentConnected ? false : state.isAgentTimeout,
        },
      });
    case types.SET_SENSITIVE_INPUT:
      return ih(state, {
        isSensitiveInput: { $set: Boolean(action.payload.isSensitiveInput) },
      });
    default:
      return state;
  }
};
